import camelcaseKeys from "camelcase-keys";
import { createContext, useContext, useEffect, useState } from "react";

import { useVaultSession } from "../../hooks/vault/base";

export type IDP = "LOCAL" | "MICROSOFT" | "AUTH0" | "PING" | "OKTA";

export type ROLES =
  | "org.admin"
  | "org.auditor"
  | "org.key.custodian"
  | "org.project.manager"
  | "sys.admin";

export type STATUS =
  | "invited"
  | "pending"
  | "active"
  | "suspended"
  | "banned"
  | "deleted";

export type User = {
  id: string;
  oid: string;
  upn: string;
  idp: IDP[];
  email: string;
  activeOrganization: {
    id: string;
    url: string;
    name: string;
    role: ROLES;
    status: "active" | "inactive";
    projects: string[];
    tos: { hash: string; version: string };
  };
  organizations: { id: string; role: ROLES; status: STATUS }[];
  lastName: string;
  firstName: string;
  lastActive: Date | null;
  session: {
    id: string;
    code: string;
    type: IDP;
    status: "active" | "inactive";
    expiresAt: null;
  };
};

export const INIT_USER: User = {
  id: "",
  oid: "",
  upn: "",
  idp: ["LOCAL"],
  email: "",
  activeOrganization: {
    id: "",
    url: "",
    name: "",
    role: "org.auditor",
    status: "inactive",
    projects: [],
    tos: { hash: "", version: "" },
  },
  organizations: [],
  lastName: "",
  firstName: "",
  lastActive: null,
  session: {
    id: "",
    code: "",
    type: "LOCAL",
    status: "inactive",
    expiresAt: null,
  },
};

const AuthContext = createContext({
  user: INIT_USER,
  auth: false,
  isInitial: true,
  isLoading: false,
  setUser: (user: User) => {},
  setAuth: (auth: boolean) => {},
});

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }: { children: any }) => {
  const [auth, setAuth] = useState<boolean>(false);
  const [user, setUser] = useState<User>(INIT_USER);
  const [isInitial, setIsInitial] = useState<boolean>(true);

  const session = useVaultSession();
  const isLoading = session.isPending;

  useEffect(() => {
    // check server for active session
    session.mutate();
  }, []);

  useEffect(() => {
    if (session.isSuccess) {
      const user = camelcaseKeys(session.data.data.user, { deep: true });

      setAuth(true);
      setUser(user);
      setIsInitial(false);

      localStorage.setItem("SEMIAUTH_AUTHENTICATED", "true");
    }
  }, [session.isSuccess]);

  return (
    <AuthContext.Provider
      value={{ auth, setAuth, user, setUser, isLoading, isInitial }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
